import { MembershipFees } from "./membershipFees";
import { useCustom, useCustomMutation, useNavigation } from "@refinedev/core";
import { useParams } from "react-router-dom";
import { Show } from "@refinedev/antd";
import {
  Badge,
  Button,
  Card,
  Descriptions,
  Form,
  Input,
  Modal,
  Popconfirm,
  Progress,
  Select,
  Space,
  Tag,
  Typography,
  message,
} from "antd";
import {
  CheckCircleTwoTone,
  CloseCircleOutlined,
  DeleteOutlined,
  DatabaseOutlined,
  EditOutlined,
  LoginOutlined,
  MailOutlined,
  UserSwitchOutlined,
} from "@ant-design/icons";
import { useState } from "react";
import { format } from "date-fns";
import { apiUrl } from "@/consts";

type InviteStatus =
  | "PENDING"
  | "ACCEPTED"
  | "DECLINED"
  | "EXPIRED"
  | "CANCELLED";

type ClubDetail = {
  sampleDataAvailable: boolean;
  club: {
    id: string;
    name: string;
    slug: string;
    status: string;
    description: string | null;
    contactPerson: string | null;
    contactEmail: string | null;
    contactPhone: string | null;
    seasonFeePercent: string | null;
    lottoCommissionPercent: string | null;
    onboardingCompletedAt: string | null;
    organizationID: string | null;
  };
  lotto?: {
    effectivePercent: number;
    globalPercent: number;
  };
  organization: { id: string; name: string } | null;
  onboarding: {
    /**
     * Ordered, self-describing steps — order and labels are owned by the
     * backend (club_onboarding/onboarding_status.ts), never hardcoded here.
     */
    steps?: Array<{ key: string; label: string; complete: boolean }>;
    /** Legacy positional flags; only used if an older API build omits `steps`. */
    step1Done: boolean;
    step2Done: boolean;
    step3Done: boolean;
    step4Done: boolean;
    step5Done: boolean;
    onboardingCompletedAt: string | null;
  };
  invite: {
    status: InviteStatus;
    expiresAt: string | null;
    recipientEmail: string | null;
    recipientName: string | null;
  } | null;
};

type Organization = { id: string; name: string };

const INVITE_STATUS_COLOR: Record<string, string> = {
  PENDING: "gold",
  ACCEPTED: "success",
  DECLINED: "red",
  EXPIRED: "default",
  CANCELLED: "default",
};

const STATUS_COLOR: Record<string, string> = {
  ACTIVE: "success",
  PENDING: "gold",
  INACTIVE: "default",
  SUSPENDED: "red",
};

function fmtDate(value: string | null | undefined) {
  if (!value) return "—";
  return format(new Date(value), "MMM d, yyyy h:mm a");
}

function StepRow({
  label,
  done,
  current,
}: {
  label: string;
  done: boolean;
  current?: boolean;
}) {
  return (
    <Space>
      {done ? (
        <CheckCircleTwoTone twoToneColor="#52c41a" />
      ) : (
        <CloseCircleOutlined
          style={{ color: current ? "#faad14" : "#bfbfbf" }}
        />
      )}
      <span style={{ color: done ? undefined : "#8c8c8c" }}>{label}</span>
      {current && <Tag color="gold">Up next</Tag>}
    </Space>
  );
}

// Fall back to the legacy positional flags only when an older API build didn't
// send `steps`. Labels here are a last resort — the backend owns the real ones.
const LEGACY_STEP_LABELS = ["Step 1", "Step 2", "Step 3", "Step 4", "Step 5"];

function resolveSteps(onboarding: ClubDetail["onboarding"] | undefined) {
  if (!onboarding) return [];
  if (onboarding.steps?.length) return onboarding.steps;
  return [
    onboarding.step1Done,
    onboarding.step2Done,
    onboarding.step3Done,
    onboarding.step4Done,
    onboarding.step5Done,
  ].map((complete, i) => ({
    key: `step${i + 1}`,
    label: LEGACY_STEP_LABELS[i],
    complete: !!complete,
  }));
}

export function ClubsShow() {
  const { id } = useParams<{ id: string }>();
  const { edit, list } = useNavigation();
  const [changeOwnerOpen, setChangeOwnerOpen] = useState(false);
  const [organizationOpen, setOrganizationOpen] = useState(false);
  const [organizationID, setOrganizationID] = useState<string>();
  const [changeOwnerForm] = Form.useForm<{
    ownerEmail: string;
    ownerName: string;
    message?: string;
  }>();

  const { data, isLoading, refetch } = useCustom<ClubDetail>({
    url: `${apiUrl}/clubs/${id}`,
    method: "get",
    queryOptions: { enabled: !!id },
  });

  const { data: organizationsData, isLoading: organizationsLoading } =
    useCustom<{
      data: Organization[];
    }>({
      url: `${apiUrl}/organizations`,
      method: "get",
      queryOptions: { enabled: organizationOpen },
    });

  const detail = data?.data;
  const club = detail?.club;
  const lotto = detail?.lotto;
  const parentOrganization = detail?.organization;
  const onboarding = detail?.onboarding;
  const invite = detail?.invite ?? null;

  const onboardingComplete = !!onboarding?.onboardingCompletedAt;

  const steps = resolveSteps(onboarding);
  const doneCount = steps.filter((s) => s.complete).length;
  // First incomplete step — highlighted as what the club still has to do.
  const currentStepIndex = steps.findIndex((s) => !s.complete);

  // Owner changes and deletion are only possible while the owner hasn't
  // accepted the invite / started onboarding (mirrors the backend guards).
  const inviteAccepted = invite?.status === "ACCEPTED";
  const canChangeOwner = !!invite && !inviteAccepted && !onboarding?.step1Done;
  const canDelete = !!detail && !inviteAccepted && !onboarding?.step1Done;

  const { mutate: resendMutate, isLoading: resending } = useCustomMutation();
  const { mutate: impersonateMutate, isLoading: impersonating } =
    useCustomMutation();
  const { mutate: changeOwnerMutate, isLoading: changingOwner } =
    useCustomMutation();
  const { mutate: deleteMutate, isLoading: deleting } = useCustomMutation();
  const { mutate: organizationMutate, isLoading: addingToOrganization } =
    useCustomMutation();
  const { mutate: sampleDataMutate, isLoading: populatingSampleData } =
    useCustomMutation();

  function handleAddToOrganization() {
    if (!organizationID) return;
    organizationMutate(
      {
        url: `${apiUrl}/clubs/${id}/organization`,
        method: "patch",
        values: { organizationID },
      },
      {
        onSuccess: (resp: any) => {
          message.success(resp?.data?.message ?? "Club added to organization");
          setOrganizationOpen(false);
          setOrganizationID(undefined);
          refetch();
        },
        onError: (err: any) =>
          message.error(
            err?.response?.data?.error ??
              err?.message ??
              "Failed to add club to organization",
          ),
      },
    );
  }

  function handleResend() {
    resendMutate(
      {
        url: `${apiUrl}/clubs/${id}/resend-invite`,
        method: "post",
        values: {},
      },
      {
        onSuccess: (resp: any) => {
          message.success(resp?.data?.message ?? "Invite resent");
          refetch();
        },
        onError: (err: any) =>
          message.error(
            err?.response?.data?.error ??
              err?.message ??
              "Failed to resend invite",
          ),
      },
    );
  }

  function handleChangeOwner(values: {
    ownerEmail: string;
    ownerName: string;
    message?: string;
  }) {
    changeOwnerMutate(
      {
        url: `${apiUrl}/clubs/${id}/owner`,
        method: "patch",
        values,
      },
      {
        onSuccess: (resp: any) => {
          message.success(
            resp?.data?.message ?? "Owner updated and invitation sent",
          );
          setChangeOwnerOpen(false);
          changeOwnerForm.resetFields();
          refetch();
        },
        onError: (err: any) =>
          message.error(
            err?.response?.data?.error ??
              err?.message ??
              "Failed to change owner",
          ),
      },
    );
  }

  function handleDelete() {
    deleteMutate(
      {
        url: `${apiUrl}/clubs/${id}`,
        method: "delete",
        values: {},
      },
      {
        onSuccess: () => {
          message.success("Club deleted");
          list("clubs");
        },
        onError: (err: any) =>
          message.error(
            err?.response?.data?.error ??
              err?.message ??
              "Failed to delete club",
          ),
      },
    );
  }

  function handleImpersonate() {
    impersonateMutate(
      {
        url: `${apiUrl}/clubs/${id}/impersonate`,
        method: "post",
        values: {},
      },
      {
        onSuccess: (resp: any) => {
          const redirectUrl = resp?.data?.redirectUrl;
          if (redirectUrl) {
            window.location.assign(redirectUrl);
          } else {
            message.error("No redirect URL returned");
          }
        },
        onError: (err: any) =>
          message.error(
            err?.response?.data?.error ??
              err?.message ??
              "Failed to impersonate",
          ),
      },
    );
  }

  function handlePopulateSampleData() {
    sampleDataMutate(
      {
        url: `${apiUrl}/clubs/${id}/sample-data`,
        method: "post",
        values: {},
      },
      {
        onSuccess: (resp: any) => {
          const result = resp?.data?.result;
          Modal.success({
            title: "Sample data populated",
            content: result ? (
              <Space direction="vertical" size={2}>
                <span>{result.growth?.bodyRows ?? 0} growth measurements</span>
                <span>
                  {result.performance?.activityRows ?? 0} training activities
                </span>
                <span>{result.sleep?.sleepRows ?? 0} sleep records</span>
                <span>
                  {result.wellness?.wellnessRows ?? 0} wellness check-ins
                </span>
                <span>
                  {result.physicalTesting?.scoreRows ?? 0} physical-testing
                  scores
                </span>
              </Space>
            ) : (
              (resp?.data?.message ?? "The club is ready to use.")
            ),
          });
        },
        onError: (err: any) =>
          message.error(
            err?.response?.data?.error ??
              err?.message ??
              "Failed to populate sample data",
          ),
      },
    );
  }

  return (
    <Show
      isLoading={isLoading}
      title="Club"
      headerButtons={
        <Space>
          {detail?.sampleDataAvailable && (
            <Popconfirm
              title="Populate sample data for this club?"
              description="This replaces previously seeded data and preserves real athlete data. It can take up to a minute."
              onConfirm={handlePopulateSampleData}
              okText="Populate"
              cancelText="Cancel"
            >
              <Button
                icon={<DatabaseOutlined />}
                loading={populatingSampleData}
              >
                Populate sample data
              </Button>
            </Popconfirm>
          )}
          <Button
            icon={<EditOutlined />}
            onClick={() => id && edit("clubs", id)}
          >
            Edit
          </Button>
          <Popconfirm
            title="Resend the invite email to the club owner?"
            onConfirm={handleResend}
            okText="Resend"
            cancelText="Cancel"
            disabled={inviteAccepted}
          >
            <Button
              icon={<MailOutlined />}
              loading={resending}
              disabled={inviteAccepted}
            >
              Resend invite
            </Button>
          </Popconfirm>
          <Button
            icon={<UserSwitchOutlined />}
            disabled={!canChangeOwner}
            title={
              canChangeOwner
                ? undefined
                : "The owner has already started onboarding"
            }
            onClick={() => {
              changeOwnerForm.setFieldsValue({
                ownerEmail: invite?.recipientEmail ?? "",
                ownerName: invite?.recipientName ?? "",
              });
              setChangeOwnerOpen(true);
            }}
          >
            Change owner
          </Button>
          <Popconfirm
            title="Delete this club?"
            description="This removes the club, its invitation, and the pending owner account."
            onConfirm={handleDelete}
            okText="Delete"
            okButtonProps={{ danger: true }}
            cancelText="Cancel"
            disabled={!canDelete}
          >
            <Button
              danger
              icon={<DeleteOutlined />}
              loading={deleting}
              disabled={!canDelete}
              title={
                canDelete
                  ? undefined
                  : "The owner has already started onboarding — suspend the club instead"
              }
            >
              Delete
            </Button>
          </Popconfirm>
          <Button
            type="primary"
            icon={<LoginOutlined />}
            loading={impersonating}
            onClick={handleImpersonate}
          >
            Login as this club
          </Button>
        </Space>
      }
    >
      {club ? (
        <Space direction="vertical" size={16} style={{ width: "100%" }}>
          <Card title="Basic info" size="small">
            <Descriptions bordered column={1} size="small">
              <Descriptions.Item label="Name">{club.name}</Descriptions.Item>
              <Descriptions.Item label="Slug">{club.slug}</Descriptions.Item>
              <Descriptions.Item label="Status">
                <Tag color={STATUS_COLOR[club.status] ?? "default"}>
                  {club.status}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Description">
                {club.description || "—"}
              </Descriptions.Item>
              <Descriptions.Item label="Contact person">
                {club.contactPerson || "—"}
              </Descriptions.Item>
              <Descriptions.Item label="Contact email">
                {club.contactEmail || "—"}
              </Descriptions.Item>
              <Descriptions.Item label="Contact phone">
                {club.contactPhone || "—"}
              </Descriptions.Item>
              <Descriptions.Item label="Organization">
                {club.organizationID ? (
                  <Space>
                    <Tag color="blue">District</Tag>
                    {parentOrganization?.name ?? club.organizationID}
                  </Space>
                ) : (
                  <Button type="link" onClick={() => setOrganizationOpen(true)}>
                    Add to organization
                  </Button>
                )}
              </Descriptions.Item>
            </Descriptions>
          </Card>

          <Card title="Owner & invite" size="small">
            <Descriptions bordered column={1} size="small">
              <Descriptions.Item label="Owner name">
                {invite?.recipientName || "—"}
              </Descriptions.Item>
              <Descriptions.Item label="Owner email">
                {invite?.recipientEmail || "—"}
              </Descriptions.Item>
              <Descriptions.Item label="Invite status">
                {invite ? (
                  <Tag color={INVITE_STATUS_COLOR[invite.status] ?? "default"}>
                    {invite.status}
                  </Tag>
                ) : (
                  "—"
                )}
              </Descriptions.Item>
              <Descriptions.Item label="Invite expires">
                {fmtDate(invite?.expiresAt)}
              </Descriptions.Item>
            </Descriptions>
          </Card>

          <Card title="Fees" size="small">
            <Descriptions bordered column={1} size="small">
              <Descriptions.Item label="Membership commission %">
                {club.seasonFeePercent != null
                  ? `${club.seasonFeePercent}%`
                  : "Default"}
              </Descriptions.Item>
              <Descriptions.Item label="Lotto commission %">
                {club.lottoCommissionPercent != null
                  ? `${Number(club.lottoCommissionPercent)}% (override)`
                  : lotto?.globalPercent != null
                    ? `Default (${lotto.globalPercent}% global)`
                    : "Default"}
              </Descriptions.Item>
              {lotto?.effectivePercent != null && (
                <Descriptions.Item label="Effective lotto rate">
                  {lotto.effectivePercent}%
                </Descriptions.Item>
              )}
            </Descriptions>
          </Card>

          <MembershipFees clubId={club.id} />

          <Card
            title="Onboarding"
            size="small"
            extra={
              <Badge
                status={onboardingComplete ? "success" : "processing"}
                text={onboardingComplete ? "Complete" : "In progress"}
              />
            }
          >
            <Space direction="vertical" size={8} style={{ width: "100%" }}>
              {steps.length > 0 && (
                <Progress
                  percent={Math.round((doneCount / steps.length) * 100)}
                  size="small"
                  status={onboardingComplete ? "success" : "active"}
                  format={() => `${doneCount}/${steps.length}`}
                />
              )}
              {steps.map((step, i) => (
                <StepRow
                  key={step.key}
                  label={step.label}
                  done={step.complete}
                  current={i === currentStepIndex}
                />
              ))}
              {onboarding?.onboardingCompletedAt && (
                <Typography.Text type="secondary">
                  Completed {fmtDate(onboarding.onboardingCompletedAt)}
                </Typography.Text>
              )}
            </Space>
          </Card>
        </Space>
      ) : null}

      <Modal
        title="Add club to organization"
        open={organizationOpen}
        onCancel={() => {
          setOrganizationOpen(false);
          setOrganizationID(undefined);
        }}
        onOk={handleAddToOrganization}
        okText="Add to organization"
        okButtonProps={{ disabled: !organizationID }}
        confirmLoading={addingToOrganization}
      >
        <Typography.Paragraph type="secondary">
          This club will become a district of the selected organization. Its
          members may become subject to that organization&apos;s consent,
          membership and announcements.
        </Typography.Paragraph>
        <Select
          showSearch
          style={{ width: "100%" }}
          placeholder="Select an organization"
          loading={organizationsLoading}
          value={organizationID}
          onChange={setOrganizationID}
          optionFilterProp="label"
          options={(organizationsData?.data?.data ?? []).map((org) => ({
            value: org.id,
            label: org.name,
          }))}
        />
      </Modal>

      <Modal
        title="Change club owner"
        open={changeOwnerOpen}
        onCancel={() => setChangeOwnerOpen(false)}
        onOk={() => changeOwnerForm.submit()}
        okText="Update & send invite"
        confirmLoading={changingOwner}
        // Mount the form up-front so setFieldsValue-before-open (prefill) works.
        forceRender
      >
        <Typography.Paragraph type="secondary">
          The invitation will be re-sent to the new email. The previous invite
          link stops working and the previous owner loses access to this club.
        </Typography.Paragraph>
        <Form
          form={changeOwnerForm}
          layout="vertical"
          onFinish={handleChangeOwner}
        >
          <Form.Item
            label="Owner email"
            name="ownerEmail"
            rules={[
              { required: true, message: "Owner email is required" },
              { type: "email", message: "Enter a valid email" },
            ]}
          >
            <Input placeholder="owner@club.com" />
          </Form.Item>
          <Form.Item
            label="Owner name"
            name="ownerName"
            rules={[{ required: true, message: "Owner name is required" }]}
          >
            <Input placeholder="Full name" />
          </Form.Item>
          <Form.Item label="Message (optional)" name="message">
            <Input.TextArea
              rows={3}
              placeholder="Included in the invite email"
            />
          </Form.Item>
        </Form>
      </Modal>
    </Show>
  );
}
